import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Users, 
  Workflow, 
  Brain, 
  Target, 
  Shield, 
  BarChart3,
  Bot,
  Zap,
  Heart
} from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const About = () => {
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('meet-ava');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  // What we bring together
  const expertise = [
    "Business Strategy",
    "Process Transformation", 
    "Artificial Intelligence",
    "Automation",
    "Technology Implementation"
  ];

  // What we consider
  const considerations = [
    { title: "People", description: "Who performs the work today?" },
    { title: "Process", description: "How does the work actually happen?" },
    { title: "Technology", description: "Which systems need to participate?" },
    { title: "Customer Experience", description: "How will the change affect customers?" },
    { title: "Risk", description: "Where should permissions, controls and human oversight exist?" },
    { title: "Business Value", description: "What measurable outcome should the investment create?" }
  ];

  // Human qualities
  const humanQualities = [
    "Judgment", "Creativity", "Empathy", "Relationships", "Leadership", "Accountability", "Purpose"
  ];

  // What makes us different
  const differences = [
    "We don't sell tools — we build systems that work",
    "Every automation is designed around a measurable business outcome",
    "Real implementation, not just advice",
    "Experience across SMBs, enterprises, and nonprofits",
    "Clear, honest communication — no jargon",
    "A warm, collaborative approach",
    "Systems designed to scale with your business"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6">
              About <span className="text-gradient">Pecaro Consulting</span>
            </h1>
            <p className="text-2xl font-semibold text-gray-700 mb-8">
              AI Strategy. AI Automation. AI Employees.
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Pecaro Consulting helps businesses use AI to eliminate repetitive work, improve customer experiences and build smarter operations — without having to build an internal AI department.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-[#0A1538] to-[#1a2d5c] rounded-2xl p-12 text-center">
              <p className="text-gray-300 mb-4">We don't believe AI should replace human potential.</p>
              <h2 className="text-3xl font-bold text-white mb-4">We Believe It Should Amplify It.</h2>
              <p className="text-xl text-[#227AFF] font-semibold">
                AI that amplifies human potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The ALTAM Story */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Building the Future of AI-Powered Work</h2>
            <div className="accent-line"></div>
            
            <div className="mt-12 space-y-6 text-lg text-gray-700">
              <p>
                Pecaro Consulting was founded with a clear vision: to help businesses access practical AI capabilities without having to build everything themselves.
              </p>
              
              <p>
                Large organizations can invest millions building custom AI capabilities. Most businesses can't — and they shouldn't have to.
              </p>

              <div className="bg-white rounded-2xl p-8 shadow-lg my-8">
                <p className="text-xl font-semibold text-center mb-6">That's why we're building <span className="text-[#227AFF]">ALTAM™</span></p>
                <p className="text-gray-600 text-center mb-6">
                  ALTAM is our AI workforce platform — designed to help businesses deploy and manage AI employees across business workflows, organizational knowledge, systems and approved AI technologies.
                </p>
                <div className="flex justify-center">
                  <Link to="/altam" className="btn btn-primary inline-flex items-center gap-2">
                    Explore ALTAM <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              
              <p className="font-semibold text-xl text-center py-4">
                <span className="text-[#227AFF]">AVA</span> is the first AI employee powered by ALTAM.
              </p>
              
              <p className="text-center">
                And we're inviting selected organizations to help us build the future of AI-powered work through the <Link to="/altam#client-zero" className="text-[#227AFF] font-semibold hover:underline">AVA Client Zero Program</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Humans + AI */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="mb-4">Humans + AI</h2>
            <div className="accent-line"></div>
            
            <p className="text-lg text-gray-600 mt-8 mb-6">
              Technology is exceptionally useful for repetition, information processing, retrieval and workflow execution.
            </p>
            
            <p className="text-lg text-gray-700 font-semibold mb-6">People bring something different.</p>
            
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {humanQualities.map((quality, index) => (
                <span key={index} className="bg-[#227AFF]/10 text-[#227AFF] px-4 py-2 rounded-full font-medium">
                  {quality}
                </span>
              ))}
            </div>

            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8">
              <p className="text-gray-600 mb-4">The future we are building isn't humans versus AI.</p>
              <p className="text-3xl font-bold text-[#0A1538] mb-4">It's Humans + AI.</p>
              <p className="text-gray-600">
                Let technology handle more of the repetitive work.<br />
                Give people more time for customers, creativity, relationships, innovation and the things that matter outside of work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Bring Together */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">What We Bring Together</h2>
            <div className="accent-line"></div>
            
            <p className="text-lg text-gray-600 text-center mt-8 mb-8">
              Pecaro brings together business strategy, project delivery, digital transformation, process improvement, automation and AI implementation.
            </p>
            
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {expertise.map((item, index) => (
                <span key={index} className="bg-white px-5 py-3 rounded-lg text-gray-700 font-medium shadow-sm">
                  {item}
                </span>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <p className="text-gray-600 text-center mb-4">to help businesses move from:</p>
              <p className="text-gray-500 italic text-center mb-4">"We should probably be using AI."</p>
              <p className="text-gray-600 text-center mb-4">to:</p>
              <p className="text-xl font-semibold text-[#0A1538] text-center">"Here's exactly where AI creates value in our business."</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Consider */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">We Look Beyond the AI Model</h2>
            <div className="accent-line"></div>
            <p className="text-lg text-gray-600 text-center mt-6 mb-8">
              Technology alone doesn't transform a business. Implementation does.
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {considerations.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-[#227AFF] mb-1">{item.title}</h4>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">Then we design the technology around the business.</p>
              <p className="text-xl font-semibold text-white">Not the other way around.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">What Makes Us Different</h2>
            <div className="accent-line"></div>
            
            <div className="mt-12">
              <p className="text-2xl font-semibold text-center mb-8">
                We don't just advise.<br />
                <span className="text-[#227AFF]">We design, implement, and optimize.</span>
              </p>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="space-y-4">
                  {differences.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-lg text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="mb-4 text-white">Our Vision</h2>
            <p className="text-2xl text-[#227AFF] font-semibold mb-8">
              The World's Most Accessible AI Employee Platform.
            </p>
            
            <div className="space-y-4 text-left max-w-2xl mx-auto mb-10">
              {[
                'We envision a future where organizations of every size can build their own AI-enabled workforce.',
                'Where AI employees work alongside people.',
                'Where repetitive work consumes less human potential.',
                'Where technology adapts to businesses instead of businesses constantly adapting to technology.',
                'Where entrepreneurs can scale without adding administrative complexity at the same rate.',
                'And where AI creates more time for people to focus on customers, creativity, relationships, innovation and life outside of work.'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#227AFF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">{item}</p>
                </div>
              ))}
            </div>
            
            <p className="text-2xl font-bold text-white">ALTAM exists to give time back to the things that matter.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6">Ready to Work Together?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Tell us where your organization is losing time, money or opportunities. We'll help you determine whether AI can create measurable value — and what to do next.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book an AI Strategy Session
              </Link>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass text-lg px-6 inline-flex items-center gap-2">
                Meet AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            
            <p className="text-sm text-gray-500">
              Start with the problem. We'll help you build the solution.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default About;
